import { Button, ButtonText } from '@/components/ui/button'
import { Center } from '@/components/ui/center'
import { Spinner } from '@/components/ui/spinner'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import { useSwitchTeam } from '@/lib/hooks/useSwitchTeam'
import { fetchFormSettingThunk } from '@/redux/slices/formSettingSlice'
import { fetchFormThunk, formSelectors } from '@/redux/slices/formSlice'
import type { QueryErrorDetails } from '@/redux/slices/queryStateType'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import SystemLayout from '@/views/layouts/SystemLayout'
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import { TriangleAlertIcon } from 'lucide-react-native'
import { useEffect } from 'react'
import { Text as RNText, View } from 'react-native'

export default function FormLayout({ children }: { children: React.ReactNode }) {
  return (
    <SystemLayout>
      <FormLayoutContent>{children}</FormLayoutContent>
    </SystemLayout>
  )
}

function FormLayoutContent({ children }: { children: React.ReactNode }) {
  const { formToken } = useLocalSearchParams<{ formToken: string }>()
  const dispatch = useAppDispatch()
  const navigation = useNavigation()

  // normal title style in form pages
  useEffect(() => {
    navigation.setOptions({
      headerLargeTitle: false,
      headerShadowVisible: false,
    })
  }, [navigation])

  useEffect(() => {
    dispatch(fetchFormThunk({ formToken }))
    dispatch(fetchFormSettingThunk({ formToken }))
  }, [formToken, dispatch])

  const formQueryState = useAppSelector(state => formSelectors.selectById(state.forms, formToken)?.queryState)

  if (!formQueryState) {
    return null
  }

  if (formQueryState.isLoading && !formQueryState.loaded) {
    return (
      <Center className="h-[50vh]">
        <Spinner />
      </Center>
    )
  }

  if (formQueryState.error) {
    return <FormError message={formQueryState.error} errorDetails={formQueryState.errorDetails} />
  }

  return children
}

function FormError({ message, errorDetails }: { message: string; errorDetails?: QueryErrorDetails }) {
  const router = useRouter()
  const switchTeam = useSwitchTeam()
  const [secondaryLabel, systemOrange] = usePlatformColor('secondaryLabel', 'systemOrange')

  return (
    <Center className="flex-1 px-10">
      <VStack space="md" className="items-center">
        <View className="w-20 h-20 rounded-full bg-orange-100 dark:bg-orange-900/30 items-center justify-center mb-2">
          <TriangleAlertIcon size={40} color={systemOrange as string} />
        </View>
        <Text size="xl" className="font-semibold">
          无法访问
        </Text>
        {errorDetails?.type === 'requireUserSwitch' ? (
          <Text size="md" style={{ color: secondaryLabel }} className="text-center">
            该表单属于{errorDetails.accountName}，请{' '}
            <RNText className="text-primary-600 text-base" onPress={() => switchTeam(errorDetails.userId)}>
              点击此处
            </RNText>{' '}
            切换身份，继续访问。
          </Text>
        ) : (
          <Text size="md" style={{ color: secondaryLabel }} className="text-center">
            {message}
          </Text>
        )}
      </VStack>
      <Button
        size="lg"
        className="w-full max-w-sm mt-10"
        onPress={() => (router.canDismiss() ? router.dismissAll() : router.replace('/'))}
      >
        <ButtonText>返回首页</ButtonText>
      </Button>
    </Center>
  )
}
